import { defineField, defineType } from 'sanity'

import { strongEmLink } from '../../data/richText'
import getPageInfo, { seoGroup } from '../objects/pageInfo'

export default defineType({
  title: 'Wheels',
  name: 'wheelLanding',
  type: 'document',
  orderings: [
    {
      title: 'Title A-Z',
      name: 'titleAsc',
      by: [{field: 'pageInfo.title', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
  ],
  fields: [
    getPageInfo({ path: 'wheelLanding' }),
    defineField({
      name: 'configuration',
      title: 'Configuration',
      type: 'reference',
      to: [
        {
          type: 'configuration',
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'overrideConfigUrl',
      type: 'link',
      title: 'Override Config URL',
      group: 'content',
    }),
    defineField({
      title: 'Design',
      name: 'design',
      type: 'reference',
      to: [{ type: 'design' }],
      options: {
        disableNew: true,
      },
      group: 'content',
    }),
    defineField({
      title: 'Hero',
      name: 'hero',
      type: 'hero',
      group: 'content',
    }),
    defineField({
      title: 'Overview',
      name: 'overview',
      type: 'wheelLandingOverview',
      group: 'content',
    }),
    defineField({
      title: 'Videos',
      name: 'videos',
      type: 'array',
      of: [{ type: 'youtubeVideo' }],
      group: 'content',
    }),
    defineField({
      title: 'Blue CTA',
      name: 'blueCTA',
      type: 'blueCTA',
      group: 'content',
    }),
    defineField({
      title: 'Features',
      name: 'features',
      type: 'array',
      of: [{ type: 'feature' }],
      group: 'content',
    }),
    defineField({
      title: 'Additional Features',
      name: 'additionalFeatures',
      type: 'array',
      of: [{ type: 'feature' }],
      validation: (rule) => rule.min(4).max(4),
      group: 'content',
    }),
    defineField({
      title: 'Fitment for You',
      name: 'fitment',
      type: 'fitmentBlock',
      group: 'content',
    }),
    defineField({
      title: 'Common block',
      name: 'commonBlock',
      type: 'reference',
      to: [{ type: 'commonPageBuilder' }],
      group: 'content',
    }),
    defineField({
      title: 'Sizes & Prices',
      name: 'sizesPrices',
      type: 'sizesPrices',
      group: 'content',
    }),
    defineField({
      title: 'Testimonials',
      name: 'testimonials',
      type: 'testimonials',
      group: 'content',
    }),
    defineField({
      title: 'Compare for Yourself',
      name: 'compare',
      type: 'compare',
      group: 'content',
    }),
    defineField({
      title: 'Footer Subscripts',
      name: 'subscripts',
      type: 'array',
      of: strongEmLink,
      group: 'content',
    }),
    defineField({
      title: 'Blog',
      name: 'blog',
      type: 'object',
      description:
        'To be displayed automatically, articles need to be tagged with this wheel/design and have "showOnWheelLandingPage" active.',
      fields: [
        defineField({
          title: 'Section title',
          name: 'title',
          type: 'string',
        }),
        defineField({
          title: 'Articles',
          name: 'articles',
          type: 'array',
          description:
            'If selected, these articles will be loaded first, along any other tagged ones. To be shown automatically, articles need to be tagged with the design and have "showOnWheelLandingPage" active.',
          of: [
            {
              type: 'reference',
              to: [{ type: 'article' }],
              options: {
                filter: ({ document, parent }) => {
                  // article should be tagged with the same design as the current Wheel landing page
                  const designId = document?.design?._ref
                  // exclude articles that are already on the page
                  const exclude = parent?.map((item: any) => item._ref)

                  const params = {
                    designId,
                    exclude,
                  }

                  return {
                    filter: `defined($params.designId)
                              && showOnWheelLandingPage
                              && (!defined($params.exclude) || !(_id in $params.exclude))
                              && count(tags[_ref == $params.designId]) > 0`,
                    params: {
                      params,
                    },
                  }
                },
              },
            },
          ],
          validation: (rule) => [
            rule.unique().error('Each article should be unique'),
          ],
        }),
        defineField({
          title: 'Button list',
          name: 'buttonList',
          type: 'buttonList',
        }),
      ],
      group: 'content',
    }),
  ],
  preview: {
    select: {
      pageInfo: 'pageInfo',
    },
    prepare(selection) {
      const { title = 'New Page', description: subtitle = '' } =
        selection.pageInfo || {}
      return {
        title,
        subtitle,
      }
    },
  },
})